import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  Avatar,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  AccountCircle,
  Save,
  Cancel,
  Edit,
  Verified,
  ArrowBack,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const EditProfilePage = () => {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    xmAccountNumber: '',
    weltradeAccountNumber: '',
    tradingFinancials: false,
    tradingSynthetics: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Initialize form data with current user profile
  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || '',
        mobileNumber: userProfile.mobileNumber || '',
        xmAccountNumber: userProfile.xmAccountNumber || '',
        weltradeAccountNumber: userProfile.weltradeAccountNumber || '',
        tradingFinancials: userProfile.tradingFinancials || false,
        tradingSynthetics: userProfile.tradingSynthetics || false,
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
    }
    
    if (formData.mobileNumber && !/^\+?[\d\s\-\(\)]+$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Invalid mobile number format';
    }
    
    if (formData.xmAccountNumber && !/^\d+$/.test(formData.xmAccountNumber)) {
      newErrors.xmAccountNumber = 'XM account number should contain only digits';
    }
    
    if (formData.weltradeAccountNumber && !/^\d+$/.test(formData.weltradeAccountNumber)) {
      newErrors.weltradeAccountNumber = 'Weltrade account number should contain only digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      await updateUserProfile(formData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <Box sx={{ pt: 12, pb: 8, minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Container maxWidth="md">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton 
                color="inherit" 
                onClick={handleCancel}
                sx={{ mr: 1 }}
              >
                <ArrowBack />
              </IconButton>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <Edit sx={{ fontSize: 30 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Edit Profile
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Update your account information
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </motion.div>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Personal Information
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          error={!!errors.firstName}
                          helperText={errors.firstName}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          error={!!errors.lastName}
                          helperText={errors.lastName}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          error={!!errors.email}
                          helperText={errors.email}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Mobile Number"
                          name="mobileNumber"
                          value={formData.mobileNumber}
                          onChange={handleInputChange}
                          error={!!errors.mobileNumber}
                          helperText={errors.mobileNumber || "Include country code (e.g., +27 123 456 7890)"}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Trading Accounts */}
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Trading Accounts
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Link your trading accounts to participate in competitions
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="XM Account Number"
                          name="xmAccountNumber"
                          value={formData.xmAccountNumber}
                          onChange={handleInputChange}
                          error={!!errors.xmAccountNumber}
                          helperText={errors.xmAccountNumber || "Your XM trading account number"}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Weltrade Account Number"
                          name="weltradeAccountNumber"
                          value={formData.weltradeAccountNumber}
                          onChange={handleInputChange}
                          error={!!errors.weltradeAccountNumber}
                          helperText={errors.weltradeAccountNumber || "Your Weltrade trading account number"}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Trading Preferences */}
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Trading Preferences
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Select your preferred trading instruments
                    </Typography>
                    
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.tradingFinancials}
                            onChange={handleInputChange}
                            name="tradingFinancials"
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              Financial Instruments
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Forex, Stocks, Indices, Commodities
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.tradingSynthetics}
                            onChange={handleInputChange}
                            name="tradingSynthetics"
                            color="secondary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              Synthetic Instruments
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Weltrade Synthetic Indices
                            </Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {/* Success Alert - positioned right above the buttons */}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert 
                      severity="success" 
                      icon={<CheckCircle />}
                      sx={{ mb: 3 }}
                    >
                      Profile updated successfully! Redirecting to dashboard...
                    </Alert>
                  </motion.div>
                )}

                <Paper sx={{ p: 3, backgroundColor: '#fafafa' }}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    justifyContent="flex-end"
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      sx={{ minWidth: 120 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      loading={loading}
                      disabled={loading}
                      sx={{ 
                        minWidth: 120,
                        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                        }
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Stack>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Box>
  );
};

export default EditProfilePage; 